import type { ContentIndex, LessonBundle } from '@ezishizi/content-schema';
import {
  DAILY_CHALLENGE,
  type ChallengeCandidate,
  type MasteryMap,
  type MasteryState,
  masteryKey,
  mergeChallengeBundle,
  mulberry32,
  selectChallengeCharacters,
} from '@ezishizi/learning-core';
import { loadIndex, loadLesson } from './content.ts';
import type { Profile } from './store/index.ts';

/**
 * Every character the child has attempted at least once, mapped to the first authored lesson
 * (in level/lesson/set order) that teaches it. `higherChinese` isn't used to filter today: the
 * content index only lists standard-list characters per set (Higher Chinese extras are omitted
 * from `sets[].chars`), so a Higher Chinese-only character simply won't be found and is dropped,
 * same as any other character with no authored lesson.
 */
export function challengeCandidates(
  index: ContentIndex,
  mastery: MasteryState[],
  higherChinese: boolean,
): ChallengeCandidate[] {
  const attempted = new Set(mastery.filter((m) => m.attempts > 0).map((m) => m.character));
  const found = new Set<string>();
  const out: ChallengeCandidate[] = [];
  for (const level of index.levels) {
    for (const lessonId of level.lessonIds) {
      const lesson = index.lessons.find((l) => l.id === lessonId);
      if (!lesson?.authored) continue;
      for (const set of lesson.sets) {
        for (const char of set.chars) {
          if (!attempted.has(char) || found.has(char)) continue;
          found.add(char);
          out.push({ character: char, lessonId: lesson.id, isWriteTarget: false });
        }
      }
    }
  }
  return out;
}

export interface ChallengeLoad {
  bundle: LessonBundle | null;
  chars: string[];
}

/**
 * Build (or rebuild, on resume) the merged bundle for a daily challenge run. Returns a null
 * bundle when the child hasn't attempted anything yet, so the runner can show the empty gate.
 */
export async function loadChallenge(
  profile: Profile,
  masteryRows: MasteryState[],
  opts?: { chars?: string[] },
): Promise<ChallengeLoad> {
  const index = await loadIndex();
  const candidates = challengeCandidates(index, masteryRows, profile.higherChinese);
  if (candidates.length === 0) return { bundle: null, chars: [] };
  const byChar = new Map(candidates.map((c) => [c.character, c]));
  const mastery: MasteryMap = new Map(
    masteryRows.map((r) => [masteryKey(r.character, r.skill), r]),
  );
  const chars =
    opts?.chars ??
    selectChallengeCharacters(
      candidates,
      mastery,
      new Date(),
      DAILY_CHALLENGE.questions,
      mulberry32(`${profile.id}:${Date.now()}`),
    );
  const lessonIds = [
    ...new Set(chars.map((c) => byChar.get(c)?.lessonId).filter((id): id is string => !!id)),
  ];
  const bundles = await Promise.all(lessonIds.map((id) => loadLesson(id)));
  const bundle = mergeChallengeBundle(bundles, new Set(chars), profile.higherChinese, {
    levelId: profile.levelId,
  });
  return { bundle, chars };
}
